import { COMPANY_INFO } from "@/info/company";
import { keywords as defKeywords } from "@/info/metadata/keywords";
import { SellerUnitsAndProjectsOne } from "@/types/sup";
import { Metadata } from "next";
import { createOgImage } from "../metadata/create-og-image";
import { s3Url } from "../s3-prefix";
import { getLocationsForCity } from "../server-location-store";
import { smartJoin } from "../smart-join";

export function formatBedrooms(bedrooms: number[]): string {
	if (!bedrooms || bedrooms.length === 0) return "";

	const sorted = [...bedrooms].sort((a, b) => a - b);
	const unique = [...new Set(sorted)];

	if (unique.length === 1) {
		return `${unique[0]}BHK`;
	}

	// Always use comma-separated format, no ranges
	return smartJoin(unique.map(b => `${b}BHK`));
}

function formatPropertyTypes(types: string[]): string {
	if (!types || types.length === 0) return "Properties";

	const mapped = types.map(type => {
		if (!type) return "";
		switch (type.toLowerCase()) {
			case "apartment":
				return "Apartments";
			case "villa":
				return "Villas";
			case "plot":
				return "Plots";
			case "penthouse":
				return "Penthouses";
			case "villament":
				return "Villaments";
			default:
				return type;
		}
	});

	return smartJoin([...new Set(mapped.filter(Boolean))]);
}

function formatPlotAreas(areas: number[]): string {
	if (!areas || areas.length === 0) return "";

	const rounded = areas.map(area => Math.round(area / 100) * 100);
	const unique = [...new Set(rounded)].sort((a, b) => a - b);

	if (unique.length === 1) {
		return `${unique[0]} sqft`;
	}

	return smartJoin(unique.map(area => `${area} sqft`));
}

function getLocationString(data: SellerUnitsAndProjectsOne): string {
	const city = data.location?.city || "bengaluru"; // Default city
	const locations = getLocationsForCity(city) || [];

	// Convert locality ID to name
	let locationName = "";
	let linkedLocalityNames: string[] = [];
	if (data.locality) {
		const locationObj = locations.find(loc => loc._id === data.locality);
		locationName = locationObj?.name || "";
		linkedLocalityNames = locationObj?.linkedLocalityNames || [];
	}

	let locationStr = locationName;
	if (linkedLocalityNames.length > 0) {
		locationStr = [locationName, ...linkedLocalityNames].filter(Boolean).join(", ");
	}

	return locationStr ? `${locationStr}, Bangalore` : "Bangalore";
}

function getConfigurationBasedTitle(data: SellerUnitsAndProjectsOne): string {
	const { configurations = [] } = data;
	const projectName = data.projectName || "";
	const locationStr = getLocationString(data);

	// Extract property types from the data to determine plot vs non-plot
	const propertyTypes = (data.propertyTypes || []).filter(Boolean);
	const isPlotProperty = propertyTypes.some(type => type.toLowerCase() === "plot");

	const titleParts: string[] = [];

	// Handle bedroom-based properties (non-plot)
	if (!isPlotProperty && configurations.length > 0) {
		const bedrooms = configurations
			.map(config => config.configuration.spaces?.bedrooms?.number)
			.filter(Boolean) as number[];

		if (bedrooms.length > 0) {
			const bedroomStr = formatBedrooms(bedrooms);
			const typeStr = formatPropertyTypes(propertyTypes);
			titleParts.push(`${bedroomStr} ${typeStr}`);
		}
	}

	// Handle plot-based properties
	if (isPlotProperty) {
		const plotAreas = data.plotAreas || [];

		if (plotAreas.length > 0) {
			const areaStr = formatPlotAreas(plotAreas);
			titleParts.push(`${areaStr} Plots`);
		} else {
			titleParts.push("Plots");
		}
	}

	const configStr = smartJoin(titleParts);

	return `${configStr} for Sale in ${projectName}${locationStr ? `, ${locationStr}` : ""}`;
}

function getSimpleTitle(data: SellerUnitsAndProjectsOne): string {
	const propertyName = data.projectName || "";
	const locationStr = getLocationString(data);

	// Handle bedroom info
	const bedrooms = data.bedrooms || [];
	const propertyTypes = (data.propertyTypes || []).filter(Boolean);

	let titlePrefix = "";

	if (bedrooms.length > 0) {
		const bedroomStr = formatBedrooms(bedrooms);
		const typeStr = formatPropertyTypes(propertyTypes);
		titlePrefix = `${bedroomStr} ${typeStr}`;
	} else if (propertyTypes.length > 0) {
		titlePrefix = formatPropertyTypes(propertyTypes);
	} else {
		titlePrefix = "Property";
	}

	return `${titlePrefix} for Sale in ${propertyName}${locationStr ? `, ${locationStr}` : ""}`;
}

function generateSupKeywords(data: SellerUnitsAndProjectsOne): string[] {
	const keywords: string[] = [];
	const city = (data.location?.city || "bengaluru").toUpperCase();
	const projectName = data.projectName || "";
	const locationStr = getLocationString(data);
	const bedrooms = data.bedrooms || [];
	const propertyTypes = (data.propertyTypes || []).filter(Boolean);
	const plotAreas = data.plotAreas || [];

	// Generate keywords for plot properties
	if (propertyTypes.includes("Plot") && plotAreas.length > 0) {
		keywords.push(
			"plots for sale",
			"plots for buy",
			"plots for sell",
			`plots in ${city}`,
			...(projectName ? [`plots in ${projectName.toLowerCase()}`] : []),
			...(locationStr ? [`plots in ${locationStr.toLowerCase()}`] : []),
		);
	}

	// Generate keywords for apartment/villa properties
	if (bedrooms.length > 0) {
		bedrooms.forEach(bedroom => {
			propertyTypes.forEach(propertyType => {
				if (propertyType === "Plot") return;
				keywords.push(
					`${bedroom}BHK ${propertyType.toLowerCase()} for sale`,
					`${bedroom}BHK ${propertyType.toLowerCase()} for buy`,
					`${bedroom}BHK ${propertyType.toLowerCase()} for sell`,
					`${bedroom}BHK ${propertyType.toLowerCase()} in ${city}`,
					...(projectName ? [`${bedroom}BHK ${propertyType.toLowerCase()} in ${projectName.toLowerCase()}`] : []),
					...(locationStr ? [`${bedroom}BHK ${propertyType.toLowerCase()} in ${locationStr.toLowerCase()}`] : []),
				);
			});
		});
	}

	// Add general property keywords
	if (propertyTypes.length > 0) {
		propertyTypes.forEach(propertyType => {
			if (propertyType !== "Plot") {
				keywords.push(
					`${propertyType.toLowerCase()} for sale`,
					`${propertyType.toLowerCase()} for buy`,
					`${propertyType.toLowerCase()} for sell`,
					`${propertyType.toLowerCase()} in ${city}`,
					...(projectName ? [`${propertyType.toLowerCase()} in ${projectName.toLowerCase()}`] : []),
					...(locationStr ? [`${propertyType.toLowerCase()} in ${locationStr.toLowerCase()}`] : []),
				);
			}
		});
	}

	// Add project-specific keywords
	if (projectName) {
		keywords.push(
			`properties in ${projectName.toLowerCase()}`,
			`${projectName.toLowerCase()} properties`,
			`${projectName.toLowerCase()} ${city.toLowerCase()}`,
			...(locationStr ? [`${projectName.toLowerCase()} ${locationStr.toLowerCase()}`] : []),
		);
	}

	return [...keywords, ...defKeywords];
}

export function getSupMetadata(params: { supData: SellerUnitsAndProjectsOne }): Metadata {
	const { supData } = params;

	if (!supData) {
		return {
			title: "Property for Sale",
			description: "Find your dream property with Settlin",
			keywords: defKeywords,
		};
	}

	let title: string;

	// Use configuration-based title if available, otherwise fall back to simple title
	if (supData.configurations && supData.configurations.length > 0) {
		title = getConfigurationBasedTitle(supData);
	} else {
		title = getSimpleTitle(supData);
	}

	// Generate description
	const locationStr = getLocationString(supData);
	const bedroomStr = formatBedrooms(supData.bedrooms);
	const propertyName = supData.projectName || "";
	const description = `Discover premium properties for sale${bedroomStr ? ` ${bedroomStr}` : ""} in ${propertyName}${locationStr ? `, ${locationStr}` : ""}. Verified listings with detailed information. Find your perfect home with Settlin.`;

	// Generate keywords based on SUP data
	const keywords = generateSupKeywords(supData);
	const url = `${COMPANY_INFO.website}/units/${supData.slug}-${supData._id}`;
	let coverImage = supData.cover ?? undefined;
	let ogImages;
	if (coverImage) {
		coverImage = coverImage.includes("http") ? coverImage : s3Url(coverImage);
		ogImages = [createOgImage(coverImage, title)];
	}

	return {
		title,
		description,
		keywords,
		alternates: { canonical: url },
		openGraph: {
			type: "website",
			title,
			description,
			url,
			siteName: "Settlin",
			images: ogImages,
		},
		twitter: {
			card: "summary",
			title,
			description,
			images: ogImages,
		},
	};
}
